import { apiConfig, http } from "@api/base";
import { getSearchTotal, prepareSumQuery } from "@api/utils";
import { sessionManager } from "@authentication/services/SessionManager";
import _isEmpty from "lodash/isEmpty";
import { generatePath } from "react-router-dom";
import { serializer } from "./serializers";

const apiPaths = {
  // Main attendance operations
  list: "/attendance/logs",
  item: "/attendance/logs/:logId",
  create: "/attendance/logs", // Check-in
  update: "/attendance/logs/:logId",
  delete: "/attendance/logs/:logId",

  // Check-in/Check-out operations
  checkIn: "/attendance/logs",
  checkOut: "/attendance/logs/:libraryId/checkout",
  userStatus: "/attendance/logs/:libraryId/status",

  // Statistics and reporting
  dailyStats: "/attendance/stats/daily",

  // Debug endpoints (for development/admin)
  debugUser: "/attendance/debug/user/:libraryId",
  debugActive: "/attendance/debug/active",
  debugCheckoutTest: "/attendance/debug/checkout-test",
  forceCheckout: "/attendance/debug/force-checkout/:libraryId",

  // Test endpoint
  test: "/attendance/test",
};

/**
 * Get a specific attendance log by ID
 */
const get = async (logId) => {
  const path = generatePath(apiPaths.item, { logId });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Check-in operation - Create new attendance record
 */
const checkIn = async (attendanceData) => {
  const currentUser = sessionManager.user;
  const backendPayload = serializer.toJSON(attendanceData);

  // Add user context
  backendPayload.created_by =
    currentUser?.username || currentUser?.email || "Frontend";

  const response = await http.post(apiPaths.checkIn, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Check-out operation - Complete attendance session
 */
const checkOut = async (libraryId) => {
  const path = generatePath(apiPaths.checkOut, { libraryId });
  const response = await http.post(path);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Get user attendance status (active session or last session)
 */
const getUserStatus = async (libraryId) => {
  const path = generatePath(apiPaths.userStatus, { libraryId });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.data) {
    // Transform the response to include serialized session data
    const statusData = response.data.data;
    if (statusData.session) {
      statusData.session = serializer.fromJSON(statusData.session);
    }
    if (statusData.last_session) {
      statusData.last_session = serializer.fromJSON(statusData.last_session);
    }
    response.data = statusData;
  }

  return response;
};

/**
 * Update an existing attendance record (admin operation)
 */
const update = async (logId, attendanceData) => {
  const currentUser = sessionManager.user;
  const path = generatePath(apiPaths.update, { logId });
  const backendPayload = serializer.toJSON(attendanceData);

  backendPayload.updated_by = currentUser?.username || currentUser?.email;

  const response = await http.put(path, backendPayload);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  } else {
    response.data = serializer.fromJSON(response.data);
  }

  return response;
};

/**
 * Delete an attendance record
 */
const deleteAttendance = async (logId) => {
  const path = generatePath(apiPaths.delete, { logId });
  const response = await http.delete(path);
  return response;
};

/**
 * Get daily attendance statistics
 */
const getDailyStats = async (date = null) => {
  const queryString = date ? `?date=${date}` : "";
  const response = await http.get(`${apiPaths.dailyStats}${queryString}`);

  if (response.data.status === "success" && response.data.data) {
    const statsData = response.data.data;
    // Serialize attendance records if present
    if (statsData.records && Array.isArray(statsData.records)) {
      statsData.records = statsData.records.map((record) =>
        serializer.fromJSON(record)
      );
    }
    response.data = statsData;
  }

  return response;
};

/**
 * Query Builder for attendance logs
 */
class AttendanceQueryBuilder {
  constructor() {
    this.libraryIdQuery = [];
    this.statusQuery = [];
    this.programQuery = [];
    this.purposeQuery = [];
    this.dateFromQuery = "";
    this.dateToQuery = "";
    this.size = "";
    this.page = "";
    this.sortBy = "";
  }

  withLibraryId(libraryId) {
    if (!libraryId || _isEmpty(libraryId)) {
      throw TypeError("Library ID argument missing");
    }
    this.libraryIdQuery.push(`library_id:"${prepareSumQuery(libraryId)}"`);
    return this;
  }

  withStatus(status) {
    if (!status || !["all", "active", "completed"].includes(status)) {
      throw TypeError("Invalid status. Use: all, active, or completed");
    }
    this.statusQuery.push(`status:"${status}"`);
    return this;
  }

  withProgram(program) {
    if (!program || _isEmpty(program)) {
      throw TypeError("Program argument missing");
    }
    this.programQuery.push(`program:"${prepareSumQuery(program)}"`);
    return this;
  }

  withPurpose(purpose) {
    if (!purpose || _isEmpty(purpose)) {
      throw TypeError("Purpose argument missing");
    }
    this.purposeQuery.push(`purpose_of_visit:"${prepareSumQuery(purpose)}"`);
    return this;
  }

  withDateFrom(dateFrom) {
    if (!dateFrom) {
      throw TypeError("Date from argument missing");
    }
    this.dateFromQuery = `date_from=${dateFrom}`;
    return this;
  }

  withDateTo(dateTo) {
    if (!dateTo) {
      throw TypeError("Date to argument missing");
    }
    this.dateToQuery = `date_to=${dateTo}`;
    return this;
  }

  activeOnly() {
    this.statusQuery.push('status:"active"');
    return this;
  }

  completedOnly() {
    this.statusQuery.push('status:"completed"');
    return this;
  }

  withSize(size) {
    if (size > 0) this.size = `per_page=${size}`;
    return this;
  }

  withPage(page = 1) {
    if (page > 0) this.page = `page=${page}`;
    return this;
  }

  sortByNewest() {
    // Backend sorts by in_time desc by default
    return this;
  }

  sortByOldest() {
    // Would need backend support for ascending sort
    return this;
  }

  qs() {
    const queryParams = [];

    // Add search criteria
    const searchCriteria = this.libraryIdQuery
      .concat(this.statusQuery)
      .concat(this.programQuery)
      .concat(this.purposeQuery)
      .join(" AND ");

    if (searchCriteria) {
      queryParams.push(`q=${encodeURIComponent(searchCriteria)}`);
    }

    // Add other parameters
    if (this.dateFromQuery) queryParams.push(this.dateFromQuery);
    if (this.dateToQuery) queryParams.push(this.dateToQuery);
    if (this.size) queryParams.push(this.size);
    if (this.page) queryParams.push(this.page);

    return queryParams.join("&");
  }
}

const queryBuilder = () => {
  return new AttendanceQueryBuilder();
};

/**
 * List attendance logs with optional filtering
 */
const list = async (query = "") => {
  const queryString = query ? `?${query}` : "";
  const response = await http.get(`${apiPaths.list}${queryString}`);

  if (response.data.status === "success" && response.data.data) {
    const attendanceLogs = Array.isArray(response.data.data)
      ? response.data.data
      : [];

    response.data.hits = attendanceLogs.map((item) =>
      serializer.fromJSON(item)
    );
    response.data.total =
      response.data.pagination?.total || attendanceLogs.length;
    response.data.pagination = response.data.pagination || {};
  } else if (response.data.hits) {
    response.data.total = getSearchTotal(response.data.hits);
    response.data.hits = response.data.hits.hits.map((hit) =>
      serializer.fromJSON(hit)
    );
  } else {
    response.data.hits = [];
    response.data.total = 0;
  }

  return response;
};

/**
 * Count attendance records matching query
 */
const count = async (query) => {
  const response = await http.get(`${apiPaths.list}?${query}`);
  response.data =
    response.data.pagination?.total || getSearchTotal(response.data.hits) || 0;
  return response;
};

/**
 * Get active sessions (users currently checked in)
 */
const getActiveSessions = async () => {
  const query = queryBuilder().activeOnly().qs();
  return await list(query);
};

/**
 * Get completed sessions for a specific date
 */
const getCompletedSessions = async (date = null) => {
  const builder = queryBuilder().completedOnly();
  if (date) {
    builder.withDateFrom(date).withDateTo(date);
  }
  const query = builder.qs();
  return await list(query);
};

/**
 * Get attendance history for a specific user
 */
const getUserHistory = async (libraryId, limit = 50) => {
  const query = queryBuilder()
    .withLibraryId(libraryId)
    .withSize(limit)
    .sortByNewest()
    .qs();
  return await list(query);
};

/**
 * Get attendance records by program
 */
const getByProgram = async (program, limit = 100) => {
  const query = queryBuilder()
    .withProgram(program)
    .withSize(limit)
    .sortByNewest()
    .qs();
  return await list(query);
};

/**
 * Get attendance records by purpose of visit
 */
const getByPurpose = async (purpose, limit = 100) => {
  const query = queryBuilder()
    .withPurpose(purpose)
    .withSize(limit)
    .sortByNewest()
    .qs();
  return await list(query);
};

/**
 * Get attendance analytics for a date range
 */
const getAnalytics = async (startDate, endDate) => {
  try {
    const builder = queryBuilder().withSize(1000); // Get more records for analytics
    if (startDate) builder.withDateFrom(startDate);
    if (endDate) builder.withDateTo(endDate);

    const query = builder.qs();
    const response = await list(query);

    if (response.data.hits) {
      // Generate analytics using serializer methods
      const analytics = serializer.generateAnalyticsReport(response.data.hits, {
        startDate,
        endDate,
      });

      return {
        status: "success",
        data: analytics,
      };
    }

    return {
      status: "error",
      message: "No data available for analytics",
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to generate analytics",
      error: error.message,
    };
  }
};

/**
 * Get purpose statistics for a given period
 */
const getPurposeStats = async (startDate = null, endDate = null) => {
  try {
    const builder = queryBuilder().withSize(1000);
    if (startDate) builder.withDateFrom(startDate);
    if (endDate) builder.withDateTo(endDate);

    const query = builder.qs();
    const response = await list(query);

    if (response.data.hits) {
      const stats = serializer.getPurposeStatistics(response.data.hits);
      return {
        status: "success",
        data: stats,
      };
    }

    return {
      status: "error",
      message: "No data available for purpose statistics",
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to get purpose statistics",
      error: error.message,
    };
  }
};

/**
 * Get program statistics for a given period
 */
const getProgramStats = async (startDate = null, endDate = null) => {
  try {
    const builder = queryBuilder().withSize(1000);
    if (startDate) builder.withDateFrom(startDate);
    if (endDate) builder.withDateTo(endDate);

    const query = builder.qs();
    const response = await list(query);

    if (response.data.hits) {
      // Calculate program statistics
      const programStats = {};
      let totalWithProgram = 0;

      response.data.hits.forEach((record) => {
        const program = record.program || "Not specified";
        programStats[program] = (programStats[program] || 0) + 1;
        if (record.program) {
          totalWithProgram++;
        }
      });

      const total = response.data.hits.length;
      const statsWithPercentages = {};

      Object.entries(programStats).forEach(([program, count]) => {
        statsWithPercentages[program] = {
          count,
          percentage: total > 0 ? Math.round((count / total) * 100) : 0,
        };
      });

      return {
        status: "success",
        data: {
          breakdown: statsWithPercentages,
          totalRecords: total,
          recordsWithProgram: totalWithProgram,
          recordsWithoutProgram: total - totalWithProgram,
          completionRate:
            total > 0 ? Math.round((totalWithProgram / total) * 100) : 0,
        },
      };
    }

    return {
      status: "error",
      message: "No data available for program statistics",
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to get program statistics",
      error: error.message,
    };
  }
};

/**
 * Export attendance data to CSV
 */
const exportToCSV = async (filters = {}) => {
  try {
    const builder = queryBuilder().withSize(10000); // Large limit for export

    // Apply filters
    if (filters.startDate) builder.withDateFrom(filters.startDate);
    if (filters.endDate) builder.withDateTo(filters.endDate);
    if (filters.program) builder.withProgram(filters.program);
    if (filters.purpose) builder.withPurpose(filters.purpose);
    if (filters.status) builder.withStatus(filters.status);
    if (filters.libraryId) builder.withLibraryId(filters.libraryId);

    const query = builder.qs();
    const response = await list(query);

    if (response.data.hits) {
      const csvData = serializer.exportToCSV(response.data.hits);
      return {
        status: "success",
        data: csvData,
        filename: `attendance_export_${
          new Date().toISOString().split("T")[0]
        }.csv`,
      };
    }

    return {
      status: "error",
      message: "No data available for export",
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to export data",
      error: error.message,
    };
  }
};

/**
 * Create quick check-in with common purposes
 */
const quickCheckIn = async (libraryId, userName, options = {}) => {
  const checkInData = serializer.createQuickCheckInData(
    libraryId,
    userName,
    options.program || "",
    options.year || null,
    options.purposeOfVisit || "Study"
  );

  return await checkIn(checkInData);
};

/**
 * Force checkout a user (admin/debug operation)
 */
const forceCheckout = async (libraryId) => {
  const path = generatePath(apiPaths.forceCheckout, { libraryId });
  const response = await http.post(path);

  if (response.data.status === "success" && response.data.data) {
    response.data = serializer.fromJSON(response.data.data);
  }

  return response;
};

/**
 * Debug: Get user status with detailed information
 */
const debugUserStatus = async (libraryId) => {
  const path = generatePath(apiPaths.debugUser, { libraryId });
  const response = await http.get(path);

  if (response.data.status === "success" && response.data.debug_info) {
    const debugInfo = response.data.debug_info;
    // Serialize attendance records in debug info
    if (debugInfo.active_session) {
      debugInfo.active_session = serializer.fromJSON(debugInfo.active_session);
    }
    if (debugInfo.all_records && Array.isArray(debugInfo.all_records)) {
      debugInfo.all_records = debugInfo.all_records.map((record) =>
        serializer.fromJSON(record)
      );
    }
  }

  return response;
};

/**
 * Debug: Get all active sessions
 */
const debugActiveSessions = async () => {
  const response = await http.get(apiPaths.debugActive);

  if (response.data.status === "success" && response.data.data) {
    response.data.data = response.data.data.map((session) =>
      serializer.fromJSON(session)
    );
  }

  return response;
};

/**
 * Test API connectivity
 */
const testConnection = async () => {
  const response = await http.get(apiPaths.test);
  return response;
};

/**
 * Get attendance summary for dashboard
 */
const getDashboardSummary = async () => {
  try {
    const [activeResponse, dailyStatsResponse] = await Promise.all([
      getActiveSessions(),
      getDailyStats(),
    ]);

    return {
      status: "success",
      data: {
        activeUsers: activeResponse.data.total || 0,
        activeSessions: activeResponse.data.hits || [],
        dailyStats: dailyStatsResponse.data || {},
        todayCheckins: dailyStatsResponse.data?.total_checkins || 0,
        todayCheckouts: dailyStatsResponse.data?.total_checkouts || 0,
        currentlyInside: dailyStatsResponse.data?.currently_inside || 0,
      },
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to fetch dashboard summary",
      error: error.message,
    };
  }
};

/**
 * Get enhanced dashboard with purpose and program insights
 */
const getEnhancedDashboard = async () => {
  try {
    const today = new Date().toISOString().split("T")[0];

    const [
      activeResponse,
      dailyStatsResponse,
      purposeStatsResponse,
      programStatsResponse,
    ] = await Promise.all([
      getActiveSessions(),
      getDailyStats(today),
      getPurposeStats(today, today),
      getProgramStats(today, today),
    ]);

    return {
      status: "success",
      data: {
        // Basic stats
        activeUsers: activeResponse.data.total || 0,
        activeSessions: activeResponse.data.hits || [],
        dailyStats: dailyStatsResponse.data || {},
        todayCheckins: dailyStatsResponse.data?.total_checkins || 0,
        todayCheckouts: dailyStatsResponse.data?.total_checkouts || 0,
        currentlyInside: dailyStatsResponse.data?.currently_inside || 0,

        // Enhanced insights
        purposeBreakdown: purposeStatsResponse.data || {},
        programBreakdown: programStatsResponse.data || {},

        // Quick stats
        mostCommonPurpose: purposeStatsResponse.data?.breakdown
          ? Object.entries(purposeStatsResponse.data.breakdown).sort(
              ([, a], [, b]) => b.count - a.count
            )[0]
          : null,
        mostCommonProgram: programStatsResponse.data?.breakdown
          ? Object.entries(programStatsResponse.data.breakdown).sort(
              ([, a], [, b]) => b.count - a.count
            )[0]
          : null,
      },
    };
  } catch (error) {
    return {
      status: "error",
      message: "Failed to fetch enhanced dashboard summary",
      error: error.message,
    };
  }
};

export const attendanceApi = {
  searchBaseURL: `${apiConfig.baseURL}${apiPaths.list}`,
  query: queryBuilder,

  // Core operations
  list,
  get,
  update,
  delete: deleteAttendance,
  count,

  // Check-in/Check-out operations
  checkIn,
  checkOut,
  getUserStatus,
  quickCheckIn,

  // Statistics and reporting
  getDailyStats,
  getDashboardSummary,
  getEnhancedDashboard,
  getAnalytics,
  getPurposeStats,
  getProgramStats,

  // Convenience methods
  getActiveSessions,
  getCompletedSessions,
  getUserHistory,
  getByProgram,
  getByPurpose,

  // Data export
  exportToCSV,

  // Debug and admin operations
  forceCheckout,
  debugUserStatus,
  debugActiveSessions,
  testConnection,

  // Serializer
  serializer,
};
